import React, { useEffect, useMemo, useState, useRef } from 'react';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import Benchmark from '../../../../../common/Benchmark/Benchmark';
import VarianceValue from '../../../../../common/VarianceValue/VarianceValue';
import styles from './Revenue.module.scss';
import sharedStyles from '../../../../BusinessPerformance/common/SharedAGCStyles.module.scss';
import { useTranslation } from 'react-i18next';
import {
  isValidLineChartData,
  useProfileParams,
  isAISummariesAccessible,
} from '../../../../../../utils/common';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import isEmpty from 'lodash/isEmpty';
import GenericTable from '../../../../../common/GenericTable/GenericTable';
import GraphCard from '../../../../../common/GraphCard/GraphCard';
import { sanitizeStackedBarData } from '../../../../../../utils/stackedBarChartDataSanitizer';
import StackedBarChart from '../../../../../common/StackedBarChart/StackedBarChart';
import { useGetAGCAIDetailsMutation } from '../../../../../../redux/services/agcAIAPI';
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import { CurrencyInputFormatter } from '../../../../../../utils/currencyFormatter';

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const Revenue: React.FC<SummaryProp> = ({ summary, loading = false }) => {
  const valueNum = typeof summary?.kpiValue === 'number' ? summary.kpiValue : 0;
  const valueCurrency = summary?.kpiCurrency ?? null;
  const benchmark = summary?.benchmark;
  const { t } = useTranslation();
  const comparisonCriteria = summary?.kpiVarianceComparisonCriteria ?? 'yoy';

  return (
    <KPITile title={t('revenue.title')} dataTestId="revenue-tile" loading={loading}>
      <div className={sharedStyles.tile_content}>
        <div className={styles.value_wrapper}>
          <div className={styles.value_content}>
            {CurrencyInputFormatter(valueCurrency, valueNum, true)}
          </div>
          <VarianceValue
            value={summary?.kpiVarianceValue ?? 0}
            dataType={summary?.kpiVarianceDataType ?? 'percentage'}
            currency={summary?.kpiCurrency}
            comparisonCriteria={comparisonCriteria}
          />
        </div>
        <div className={styles.bar_wrapper}>
          <Benchmark
            minValue={benchmark?.min}
            maxValue={benchmark?.max}
            averageValue={benchmark?.mid}
            medianValue={benchmark?.median}
            quartileLine={[benchmark?.lowerQuartile ?? 0, benchmark?.upperQuartile ?? 0]}
            currentBenchmarkValue={valueNum}
            benchmarkDataType={'currency'}
            currency={summary?.kpiCurrency}
            isPercentValue={false}
          />
        </div>
      </div>
    </KPITile>
  );
};

interface RevenueMonthlyViewProps {
  viewMode: ViewMode;
  lineChartData: any;
  tableData: any;
  currency: string;
  chartSummaryData: any;
  kpiCode: string;
}

const RevenueMonthlyView: React.FC<RevenueMonthlyViewProps> = ({
  viewMode,
  lineChartData,
  tableData,
  currency,
  chartSummaryData,
  kpiCode,
}) => {
  const { t } = useTranslation();

  if (viewMode === 'left') {
    return isEmpty(lineChartData) ? (
      <div className={sharedStyles.no_data}>{t('noDataAvailable')}</div>
    ) : (
      <GraphCard
        lineChartData={lineChartData}
        currency={currency}
        chartSummaryData={chartSummaryData}
      />
    );
  }

  return isEmpty(tableData) ? (
    <div className={sharedStyles.no_data}>{t('noDataAvailable')}</div>
  ) : (
    <GenericTable
      tableData={tableData}
      kpiCode={kpiCode}
      highlightRows={[0]}
      currency={currency}
      isOnlyNegativeHighlight={true}
    />
  );
};

interface RevenueYearlyViewProps {
  viewMode: ViewMode;
  stackedBarChartData: any;
  tableData: any;
  currency: string;
  kpiCode: string;
  yAxisValues?: number[];
}

const RevenueYearlyView: React.FC<RevenueYearlyViewProps> = ({
  viewMode,
  stackedBarChartData,
  tableData,
  currency,
  kpiCode,
  yAxisValues,
}) => {
  const { t } = useTranslation();

  if (viewMode === 'left') {
    return isEmpty(stackedBarChartData) ? (
      <div className={sharedStyles.no_data}>{t('noDataAvailable')}</div>
    ) : (
      <StackedBarChart data={stackedBarChartData} currency={currency} yAxisValues={yAxisValues} />
    );
  }

  return isEmpty(tableData) ? (
    <div className={sharedStyles.no_data}>{t('noDataAvailable')}</div>
  ) : (
    <GenericTable
      tableData={tableData}
      kpiCode={kpiCode}
      highlightColumns={[1]}
      currency={currency}
    />
  );
};

export const RevenueDetails: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left');
  const [periodCode, setPeriodCode] = useState<string>('monthly');
  const kpiCode = 'revenue';
  const { affiliateCode, aboNumber } = useProfileParams();

  const { data: detailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { data: aiDetailsResp }] = useGetAGCAIDetailsMutation();
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({ affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);

  const aiRaw = aiDetailsResp?.body;
  const aiResolved = typeof aiRaw === 'string' ? aiRaw : aiRaw?.[kpiCode] || aiRaw?.summary || '';
  const baseAI = aiResolved ? String(aiResolved).slice(0, 300).concat('...') : '';
  const aiContent = baseAI
    ? `${periodCode === 'monthly' ? '[Monthly] ' : '[Yearly] '}${baseAI}`
    : '';

  const detailData: any = detailsResponse?.body?.kpis?.[0] || {};
  const currency = detailData?.kpiCurrency || 'USD';

  // Extract monthly view data
  const monthlyCharts = detailData?.detail?.monthlyView?.chart || [];
  const monthlyLineChart = monthlyCharts.find((chart: any) => chart?.chartType === 'lineChart');
  const monthlyTableChart = monthlyCharts.find((chart: any) => chart?.chartType === 'table');
  const lineChartData = monthlyLineChart?.chartDetail?.lineChartDetail;
  const monthlyTableData = monthlyTableChart?.chartDetail?.tableDetail;

  // Extract annually view data (handle typo "anuallyView" in API response)
  const annuallyCharts = detailData?.detail?.anuallyView?.chart || [];
  const annuallyBarChart = annuallyCharts.find(
    (chart: any) => chart?.chartType === 'barChart' || chart?.chartType === 'stackedBarChart'
  );
  const annuallyTableChart = annuallyCharts.find((chart: any) => chart?.chartType === 'table');
  const barChartDetail =
    annuallyBarChart?.chartDetail?.barChartDetail ||
    annuallyBarChart?.chartDetail?.stackedBarChartDetail;
  const annuallyTableData = annuallyTableChart?.chartDetail?.tableDetail;

  // Transform barChartDetail to StackedBarChart format if needed
  const stackedBarChartData = useMemo(
    () => sanitizeStackedBarData(barChartDetail, t('revenue.title')),
    [barChartDetail, t]
  );

  const yAxisValues = useMemo(() => {
    if (barChartDetail?.yAxis && Array.isArray(barChartDetail.yAxis)) {
      return barChartDetail.yAxis
        .map((y: any) => Number(y.dataValue))
        .filter((v: number) => !isNaN(v));
    }
    return undefined;
  }, [barChartDetail]);

  const tableData = periodCode === 'monthly' ? monthlyTableData : annuallyTableData;
  const hasValidLineChart = isValidLineChartData(lineChartData);
  const hasChartData = periodCode === 'monthly' ? hasValidLineChart : !isEmpty(stackedBarChartData);

  useEffect(() => {
    if (!hasChartData) {
      setViewMode('right');
    }
  }, [hasChartData]);

  const periodItems = useMemo(
    () => [
      { code: 'monthly', label: t('monthly_view') },
      { code: 'yearly', label: t('yearly_view') },
    ],
    [t]
  );

  if (isLoading) return <Skeleton height="400px" />;

  const headerContent = (
    <div className={sharedStyles.header_content}>
      <div className={sharedStyles.tile_header}>
        <div className={sharedStyles.title}>{t('revenue.title')}</div>
        <div className={sharedStyles.desc}>{t('revenue.description')}</div>
      </div>
      {hasChartData && (
        <div className={styles.chart_view_wrapper}>
          <ViewModeSwitcher
            mode={viewMode}
            onChangeMode={setViewMode}
            disabled={false}
            timeRanges={periodItems}
            selectedTimeRangeCode={periodCode}
            onSelectTimeRange={setPeriodCode}
            showSwitch={hasChartData}
          />
        </div>
      )}
    </div>
  );

  const leftContent =
    periodCode === 'monthly' ? (
      <RevenueMonthlyView
        viewMode={viewMode}
        lineChartData={lineChartData}
        tableData={tableData}
        currency={currency}
        chartSummaryData={monthlyLineChart?.chartSummary}
        kpiCode={kpiCode}
      />
    ) : (
      <RevenueYearlyView
        viewMode={viewMode}
        stackedBarChartData={stackedBarChartData}
        tableData={tableData}
        currency={currency}
        kpiCode={kpiCode}
        yAxisValues={yAxisValues}
      />
    );

  const rightContent = isAISummariesAccessible() ? (
    <InsightAI
      updateDate=""
      content={aiContent}
      title={t('ai_summary')}
      showDropdown={false}
      isDropdownEnabled={true}
      flowerIconSize={20}
      infoIconSize={12}
      titleSize={14}
    />
  ) : null;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="renewal-rate-detail"
    />
  );
};

export default Revenue;
